import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CircleListResponse, EnrollmentSummary, Gender } from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { genderLabel } from "../lib/labels";
import { useMe } from "../lib/session";
import { ErrorNote, Field, Modal, useSubmit } from "./ui";

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all(
    ["users", "circles", "overview", "branches"].map((k) => qc.invalidateQueries({ queryKey: [k] })),
  );

/** Transfer / end buttons for one active enrollment. */
export function EnrollmentActions({
  enrollment,
  studentName,
  studentGender,
}: {
  enrollment: EnrollmentSummary;
  studentName: string;
  studentGender: Gender | null;
}) {
  const [mode, setMode] = useState<"transfer" | "end" | null>(null);
  return (
    <div className="row-actions">
      <button className="btn btn-ghost btn-sm" onClick={() => setMode("transfer")}>
        نقل
      </button>
      <button className="btn btn-ghost btn-sm" onClick={() => setMode("end")}>
        إنهاء
      </button>
      <TransferModal
        open={mode === "transfer"}
        onClose={() => setMode(null)}
        enrollment={enrollment}
        studentName={studentName}
        studentGender={studentGender}
      />
      <EndModal
        open={mode === "end"}
        onClose={() => setMode(null)}
        enrollment={enrollment}
        studentName={studentName}
      />
    </div>
  );
}

function TransferModal({
  open,
  onClose,
  enrollment,
  studentName,
  studentGender,
}: {
  open: boolean;
  onClose: () => void;
  enrollment: EnrollmentSummary;
  studentName: string;
  studentGender: Gender | null;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { busy, error, run } = useSubmit();
  const [circleId, setCircleId] = useState("");
  const [reason, setReason] = useState("");
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "all"],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
    enabled: open,
  });
  const choices = (circles.data?.circles ?? []).filter(
    (c) => c.id !== enrollment.circleId && (!c.gender || !studentGender || c.gender === studentGender),
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", `/v1/enrollments/${enrollment.id}/transfer`, {
        circleId,
        reason: reason.trim() || undefined,
      });
      await invalidate(qc);
      onClose();
    }, errorText);
  }

  return (
    <Modal title={`نقل ${studentName}`} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <p className="muted">
          من: <b>{enrollment.circleName}</b> · {enrollment.branchName}. يبقى التسجيل السابق في السجل بحالة
          «منقول».
        </p>
        <Field id="t-circle" label="إلى الحلقة">
          <select id="t-circle" value={circleId} onChange={(e) => setCircleId(e.target.value)} required>
            <option value="">اختر الحلقة</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.branchName}
                {c.gender ? ` (${genderLabel[c.gender]})` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field id="t-reason" label="السبب (اختياري)">
          <input id="t-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
        </Field>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !circleId}>
            {busy ? "جارٍ النقل…" : "نقل الطالب"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EndModal({
  open,
  onClose,
  enrollment,
  studentName,
}: {
  open: boolean;
  onClose: () => void;
  enrollment: EnrollmentSummary;
  studentName: string;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [status, setStatus] = useState<"withdrawn" | "completed">("withdrawn");
  const [reason, setReason] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", `/v1/enrollments/${enrollment.id}/end`, {
        status,
        reason: reason.trim() || undefined,
      });
      await invalidate(qc);
      onClose();
    }, errorText);
  }
  return (
    <Modal title={`إنهاء تسجيل ${studentName}`} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <p className="muted">
          في حلقة <b>{enrollment.circleName}</b>. يبقى التسجيل وسجل الطالب محفوظين.
        </p>
        <Field id="e-status" label="الحالة">
          <select id="e-status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="withdrawn">انسحاب</option>
            <option value="completed">إكمال</option>
          </select>
        </Field>
        <Field id="e-reason" label="السبب (اختياري)">
          <input id="e-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
        </Field>
        <div className="form-actions">
          <button className="btn btn-danger" disabled={busy}>
            {busy ? "جارٍ الحفظ…" : "إنهاء التسجيل"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
